import { TbMail, TbBell, TbWebhook } from "react-icons/tb";
import { SiJira } from "react-icons/si";
import { FaSlack } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTooltipProps } from "@/components/custom/tooltip";
import { cn } from "@/lib/utils";
import { ConfiguredAlertChannelDto } from "@/client";

// Shared with the Alert Channels settings page (alert-management/alert-channels/route.tsx) so both
// surfaces stay visually consistent instead of drifting apart with separate copies of this logic.

export const getChannelIcon = (channelName: string) => {
  switch (channelName?.toLowerCase()) {
    case "email":
      return TbMail;
    case "jira":
      return SiJira;
    case "slack":
      return FaSlack;
    case "webhook":
      return TbWebhook;
    default:
      return TbBell;
  }
};

// Metric alerts have no webhook delivery yet, and the backend rejects a webhook channel on one with a
// 400, so the metric-alert channel pickers leave them out.
export const supportsMetricAlerts = (channel: ConfiguredAlertChannelDto) =>
  channel.channelType !== "webhook";

export interface ChannelSublineInfo {
  text: string;
  isLink: boolean;
  url?: string;
  fullText?: string;
}

export const getChannelSublineInfo = (
  channelType: string,
  rawData: any,
): ChannelSublineInfo | null => {
  const normalizedType = channelType?.toUpperCase();

  if (normalizedType === "JIRA") {
    const url = rawData?.url;
    const project = rawData?.configurationInfo?.project;
    if (url && project) {
      const jiraUrl = `${url}/browse/${project}`;
      return { text: jiraUrl, isLink: true, url: jiraUrl };
    }
  } else if (normalizedType === "EMAIL") {
    const mailingList = rawData?.configurationInfo?.mailingList;
    if (mailingList && Array.isArray(mailingList) && mailingList.length > 0) {
      const emailDisplay =
        mailingList.length <= 3
          ? mailingList.join(", ")
          : `${mailingList.slice(0, 3).join(", ")}...`;
      const fullEmailList = mailingList.join("<br />");
      return {
        text: emailDisplay,
        isLink: false,
        fullText: fullEmailList,
      };
    }
  } else if (normalizedType === "WEBHOOK") {
    // The host is enough to tell endpoints apart; the full URL can carry paths and query strings
    // nobody needs on a card.
    const url = rawData?.url;
    let host: string | null = null;
    try {
      host = url ? new URL(url).host : null;
    } catch {
      host = url ?? null;
    }
    if (host) {
      return { text: host, isLink: false };
    }
  }

  return null;
};

interface ChannelLabelProps {
  channel: ConfiguredAlertChannelDto;
  className?: string;
}

// Icon + name (blue if global, default otherwise) + per-type content subline (Jira project link,
// email mailing list truncated with a tooltip for the full list) - matches the Alert Channels cards.
export const ChannelLabel = ({ channel, className }: ChannelLabelProps) => {
  const { t } = useTranslation();
  const tooltipProps = useTooltipProps();
  const Icon = getChannelIcon(channel.channelType);
  const sublineInfo = getChannelSublineInfo(channel.channelType, channel);
  // An account can hold several channels of one type, so a user-supplied name is what tells them
  // apart. Unnamed channels keep the previous type-derived label, which is how every channel
  // created before naming existed still renders.
  const displayName =
    channel.name ??
    (!channel.isGlobal && channel.channelType === "email"
      ? t("accountsEmail")
      : channel.channelType);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-azure flex-shrink-0" />
        <span className={channel.isGlobal ? "text-azure font-semibold" : ""}>
          {channel.isGlobal ? `Edgify (${displayName})` : displayName}
        </span>
      </div>
      {sublineInfo &&
        (sublineInfo.isLink ? (
          <a
            href={sublineInfo.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-muted-foreground truncate"
          >
            {sublineInfo.text}
          </a>
        ) : (
          <div
            className="text-xs text-muted-foreground truncate"
            {...(sublineInfo.fullText ? tooltipProps : {})}
            data-tooltip-html={sublineInfo.fullText}
          >
            {sublineInfo.text}
          </div>
        ))}
    </div>
  );
};
